import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faSearch,
  faEye,
  faEdit,
  faTrashAlt,
  faFilter,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { Assistance } from '../../types/Assistance';
import { Beneficiary } from '../../types/Beneficiary';
import { getAssistances, deleteAssistance } from '../../services/assistanceService';
import { getBeneficiary } from '../../services/beneficiaryService';
import './AssistanceList.css';

const AssistanceList: React.FC = () => {
  const [assistances, setAssistances] = useState<Assistance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    fetchAssistances();
  }, []);

  const loadBeneficiaryData = async () => {
    if (loading || assistances.length === 0) return;
    
    const updatedAssistances = [...assistances];
    let hasChanges = false;
    
    for (let i = 0; i < updatedAssistances.length; i++) {
      const assistance = updatedAssistances[i];
      
      if (typeof assistance.beneficiary === 'number' && !assistance.beneficiary_details) {
        try {
          const beneficiaryData = await getBeneficiary(assistance.beneficiary);
          
          updatedAssistances[i] = {
            ...assistance,
            beneficiary_details: {
              id: beneficiaryData.id,
              first_name: beneficiaryData.first_name,
              last_name: beneficiaryData.last_name,
              nickname: beneficiaryData.nickname
            }
          };
          
          hasChanges = true;
        } catch (error) {
          console.error(`Error cargando beneficiario ${assistance.beneficiary}:`, error);
        }
      }
    }
    
    if (hasChanges) {
      setAssistances(updatedAssistances);
    }
  };

  useEffect(() => {
    loadBeneficiaryData();
  }, [assistances.length]);

  const fetchAssistances = async () => {
    try {
      setLoading(true);
      const data = await getAssistances();
      setAssistances(data);
      setError(null);
    } catch (err) {
      console.error('Error al cargar asistencias:', err);
      setError('No se pudieron cargar las asistencias. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      
      const searchParams: any = {};
      
      if (searchTerm.trim()) {
        searchParams.search = searchTerm;
      }
      
      if (filters.dateFrom) {
        searchParams.date_from = filters.dateFrom;
      }
      
      if (filters.dateTo) {
        searchParams.date_to = filters.dateTo;
      }
      
      const data = await getAssistances(searchParams);
      setAssistances(data);
      setError(null);
    } catch (err) {
      console.error('Error en la búsqueda de asistencias:', err);
      setError('No se pudo realizar la búsqueda. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (id: number | undefined) => {
    if (!id) return;
    
    if (window.confirm('¿Está seguro de que desea eliminar esta asistencia? Esta acción no se puede deshacer.')) {
      try {
        await deleteAssistance(id);
        setAssistances(assistances.filter(a => a.id !== id));
      } catch (err) {
        console.error('Error al eliminar asistencia:', err);
        setError('No se pudo eliminar la asistencia. Por favor, intente nuevamente.');
      }
    }
  };

  const handleReset = () => {
    setSearchTerm('');
    setFilters({
      dateFrom: '',
      dateTo: ''
    });
    fetchAssistances();
  };

  const formatBeneficiaryName = (assistance: Assistance) => {
    if (!assistance) return 'Sin beneficiario';
    
    if (assistance.beneficiary_details) {
      const { first_name, last_name, nickname } = assistance.beneficiary_details;
      
      let displayName = '';
      
      if (first_name || last_name) {
        displayName = `${first_name || ''} ${last_name || ''}`.trim();
      } else if (nickname) {
        displayName = `'${nickname}'`;
      } else {
        displayName = `Beneficiario ${assistance.beneficiary_details.id || ''}`;
      }
      
      if (nickname && (first_name || last_name)) {
        displayName += ` '${nickname}'`;
      }
      
      return displayName;
    }
    
    if (typeof assistance.beneficiary === 'object' && assistance.beneficiary) {
      try {
        const beneficiary = assistance.beneficiary as any;
        let name = `${beneficiary.first_name || ''} ${beneficiary.last_name || ''}`.trim();
        
        if (beneficiary.nickname) {
          if (name) {
            name += ` '${beneficiary.nickname}'`;
          } else {
            name = `'${beneficiary.nickname}'`;
          }
        }
        
        return name || `ID: ${beneficiary.id || ''}`;
      } catch (err) {
        return 'Error formato beneficiario';
      }
    }
    
    if (typeof assistance.beneficiary === 'number') {
      return `ID: ${assistance.beneficiary}`;
    }
    
    return 'Sin datos beneficiario';
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'No registrada';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  return (
    <div className="assistance-list-container">
      <div className="flex justify-between align-center mb-4">
        <h1>Asistencias</h1>
        <Link to="/assistances/create" className="btn btn-primary">
          <FontAwesomeIcon icon={faPlus} /> Nueva Asistencia
        </Link>
      </div>

      <div className="filters card mb-4">
        <h3 className="mb-2">Filtros</h3>
        <div className="filter-row">
          <div className="form-group">
            <label className="form-label">Buscar</label>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por beneficiario o notas"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="filter-row">
          <div className="form-group">
            <label className="form-label">Fecha desde</label>
            <input
              type="date"
              className="form-control"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Fecha hasta</label>
            <input
              type="date"
              className="form-control"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            />
          </div>
          <div className="form-group flex-end">
            <div className="btn-group">
              <button className="btn btn-secondary" onClick={handleReset}>
                Limpiar
              </button>
              <button className="btn btn-primary" onClick={handleSearch}>
                <FontAwesomeIcon icon={faSearch} /> Buscar
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger mb-4">{error}</div>}

      <div className="card">
        {loading ? (
          <div className="text-center p-4">Cargando asistencias...</div>
        ) : assistances.length === 0 ? (
          <div className="text-center p-4">No se encontraron asistencias.</div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Beneficiario</th>
                  <th>Autor</th>
                  <th>Fecha</th>
                  <th>Seguimiento</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {assistances.map((assistance) => (
                  <tr key={assistance.id}>
                    <td>{formatBeneficiaryName(assistance)}</td>
                    <td>{assistance.author}</td>
                    <td>{formatDate(assistance.created_at)}</td>
                    <td>
                      {assistance.extended_assistance ? (
                        <span className="status-badge status-pending">Requiere</span>
                      ) : (
                        <span className="status-badge status-delivered">No requiere</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Link 
                          to={`/assistances/${assistance.id}`} 
                          className="icon-button" 
                          title="Ver detalles"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Link>
                        <Link 
                          to={`/assistances/${assistance.id}/edit`} 
                          className="icon-button" 
                          title="Editar"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Link>
                        <button
                          className="icon-button"
                          title="Eliminar"
                          onClick={() => handleDelete(assistance.id)}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex justify-between align-center mt-4">
        <div>
          Mostrando {assistances.length} asistencias
        </div>
        <ul className="pagination">
          <li><button className="page-link"><FontAwesomeIcon icon={faChevronLeft} /></button></li>
          <li><button className="page-link active">1</button></li>
          <li><button className="page-link">2</button></li>
          <li><button className="page-link"><FontAwesomeIcon icon={faChevronRight} /></button></li>
        </ul>
      </div>
    </div>
  );
};

export default AssistanceList;